import React, { useMemo, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Typography,
} from '@mui/material';
import { getActiveEntrances, getCheckInEntranceName, resolveExitSelection } from 'src/utils/entranceSelection';

const EntranceRequestDialog = ({
    open,
    onClose,
    onConfirm,
    booking,
    title = 'Request Your Car',
    confirmLabel = 'Confirm',
    loading = false,
    mode = 'request',
}) => {
    const entrances = useMemo(
        () => getActiveEntrances(booking?.branchEntrances || booking?.branch?.branchEntrances || []),
        [booking],
    );
    const checkInName = getCheckInEntranceName(booking);
    const [sameAsCheckIn, setSameAsCheckIn] = useState('same');
    const [exitEntranceId, setExitEntranceId] = useState('');

    const selection = resolveExitSelection({
        sameAsCheckIn: sameAsCheckIn === 'same',
        selectedId: exitEntranceId,
        checkInEntranceId: booking?.checkInEntranceId || booking?.checkInEntrance?.id,
        entrances,
    });

    const pickupName = sameAsCheckIn === 'different'
        ? (selection.autoName || entrances.find((entrance) => entrance.id === Number(exitEntranceId))?.name)
        : null;

    const handleConfirm = () => {
        if (selection.needsPick) {
            return;
        }
        onConfirm({
            sameAsCheckIn: sameAsCheckIn === 'same',
            exitEntranceId: selection.exitEntranceId,
        });
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ fontWeight: 700, fontSize: 22, pb: 1 }}>
                {title}
            </DialogTitle>
            <DialogContent>
                {checkInName ? (
                    <Typography sx={{ mb: 2 }}>
                        You checked in at the: <strong>{checkInName}</strong>
                    </Typography>
                ) : null}
                <Typography sx={{ mb: 1 }}>
                    Where would you like your car delivered?
                </Typography>
                <RadioGroup
                    value={sameAsCheckIn}
                    onChange={(event) => {
                        setSameAsCheckIn(event.target.value);
                        setExitEntranceId('');
                    }}
                >
                    <FormControlLabel value="same" control={<Radio />} label={checkInName ? `Same (${checkInName})` : 'Same'} />
                    <FormControlLabel value="different" control={<Radio />} label="Different" />
                </RadioGroup>
                {pickupName ? (
                    <Box
                        sx={{
                            mt: 2,
                            py: 1.5,
                        }}
                    >
                        <Typography>
                            Your car will be ready for pick up at{'\u00A0'}the:
                            <Typography component="span" display="block" fontWeight={700}>
                                {pickupName}
                            </Typography>
                        </Typography>
                    </Box>
                ) : null}
                {sameAsCheckIn === 'different' && selection.needsPick ? (
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Exit Entrance</InputLabel>
                        <Select
                            label="Exit Entrance"
                            value={exitEntranceId}
                            onChange={(event) => setExitEntranceId(event.target.value)}
                        >
                            {entrances
                                .filter((entrance) => entrance.id !== (booking?.checkInEntranceId || booking?.checkInEntrance?.id))
                                .map((entrance) => (
                                    <MenuItem key={entrance.id} value={entrance.id}>
                                        {entrance.name}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                ) : null}
                {mode === 'update' && booking?.exitEntrance?.name ? (
                    <Typography sx={{ mt: 2 }} color="text.secondary">
                        Current Exit Entrance: {booking.exitEntrance.name}
                    </Typography>
                ) : null}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="secondary">Cancel</Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={loading || selection.needsPick}
                    sx={{ fontWeight: 700 }}
                >
                    {confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EntranceRequestDialog;
